/**
 * The self-funding paywall, in the consumer flow's own chrome.
 *
 * Never paid: the old D2C paywall treatment with the Checkout Session's amount and "available for 365
 * days" wording; buying goes to the checkout step. Expired: the same offer with a notice that access
 * ended and that every account is saved, and "Renew self-funding access". Active: no offer and no way
 * to buy again; "Continue my self-funding plan" goes into the sequence where the consumer left off.
 * No price is written here; it always comes from Stripe.
 */
import { Alert, AlertDescription, AlertTitle } from '@design-system';
import { D2cPaywallOffer } from '@/features/client-onboarding/components/d2c-paywall-offer';
import { Checklist, type ChecklistItem } from '../components/checklist';
import { StepFrame } from '../components/step-frame';
import { useSelfFundPaywall } from '../hooks/use-self-fund-paywall';
import type { ConsumerStepProps } from './step-props';

const INCLUDED: ChecklistItem[] = [
  {
    marker: 'done',
    title: 'Project what you’ll need, from any account',
    description: 'Add savings, investments, retirement accounts, income or property, and see how they fund your care.',
  },
  { marker: 'done', title: 'Your care cost breakdown', description: 'Where care happens and what each phase is likely to cost.' },
  { marker: 'done', title: 'How family is predicted to step in', description: 'The care your family could realistically give, and what it saves.' },
  { marker: 'done', title: 'A PDF of your full plan', description: 'Download it to keep or share with family.' },
  {
    marker: 'done',
    title: 'A complimentary 15-minute planning session',
    description: 'Talk your plan through with one of our LTC Planning Experts.',
  },
];

const ACCESS_TERMS = 'Available for 365 days. One-time payment, no automatic renewal or recurring charges.';

function formatLongDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function IncludedList() {
  return (
    <Checklist
      items={INCLUDED}
      doneMarkerClassName="text-primary flex size-6 shrink-0 items-center justify-center"
    />
  );
}

export function SelfFundStep({ clientId }: ConsumerStepProps) {
  const paywall = useSelfFundPaywall(clientId);
  const endDate = formatLongDate(paywall.endsAt);
  const isExpired = paywall.access === 'expired';

  return (
    <StepFrame
      step="self-fund"
      title="Want to self-fund the entire care event?"
      subtitle="Unlock a self-guided flow to build a comprehensive, personalized self-funding plan."
      onBack={paywall.back}
      help={
        <>
          <p>Self-funding access is a one-time payment for 365 days. There’s no automatic renewal and no recurring charge.</p>
          <p>While you have access, you can book your planning session any time from the top of the page.</p>
          <p>Insurance options stay available: the plan can show you more efficient ways to protect your savings.</p>
        </>
      }
      primary={paywall.canBuy ? undefined : { label: 'Continue my self-funding plan', onClick: paywall.continuePlan }}
      next={paywall.canBuy ? 'pay securely, then start planning.' : 'add your accounts and see your projection.'}
    >
      {paywall.canBuy ? (
        <D2cPaywallOffer
          hideHeading
          isAgeIneligible={paywall.isAgeIneligible}
          notice={
            isExpired && (
              <Alert>
                <AlertTitle>Your self-funding access ended{endDate ? ` on ${endDate}` : ''}</AlertTitle>
                <AlertDescription>Your accounts and plan are saved. Renew to pick up where you left off.</AlertDescription>
              </Alert>
            )
          }
          valuePropsTitle="What’s included"
          valueProps={<IncludedList />}
          primaryAction={{
            label: isExpired ? 'Renew self-funding access' : 'Unlock self-funding planning',
            onClick: paywall.startCheckout,
          }}
          priceLine={
            paywall.price ? (
              <>
                <span className="font-semibold text-gray-950">{paywall.price}</span> one-time payment.{' '}
                {ACCESS_TERMS}
              </>
            ) : (
              <>
                {ACCESS_TERMS}{' '}
                {paywall.isPriceLoading ? 'Loading the price…' : 'You’ll see the price before you pay.'}
              </>
            )
          }
        />
      ) : (
        <div className="space-y-4">
          {paywall.access === 'active' && (
            <p className="text-foreground">
              {endDate ? `You have self-funding access until ${endDate}.` : 'You have self-funding access.'}
            </p>
          )}
          <IncludedList />
        </div>
      )}
    </StepFrame>
  );
}
